/**
 * ProductDetailModal - Comprehensive product analysis modal
 * Shows variants, analytics, velocity tracking, and AI insights
 */

import React, { useState, useEffect } from 'react';
import { X, Package, TrendingUp, BarChart3, Sparkles, ChevronLeft, ChevronRight, ExternalLink, Loader2, AlertCircle, CheckCircle, Clock, DollarSign, Users, Box, Truck, Video, Image, Download, Check, Eye, RotateCw, View } from 'lucide-react';
import { DepthParallaxViewer } from './DepthParallaxViewer';
import { ProductSpinViewer } from './ProductSpinViewer';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

// Types
interface Variant {
  vid: string;
  sku: string;
  name: string;
  image: string;
  price: number;
  suggestedPrice: number;
  weight: number;
  dimensions: { length: number; width: number; height: number };
  properties: string;
  inventory: number | null;
}

interface InventoryDetail {
  summary: Array<{
    countryCode: string;
    countryNameEn: string;
    totalInventoryNum: number;
    cjInventoryNum: number;
    factoryInventoryNum: number;
  }>;
  perVariant: Array<{
    vid: string;
    inventory: Array<{
      countryCode: string;
      totalInventory: number;
      cjInventory: number;
      factoryInventory: number;
    }>;
  }>;
}

interface ProductData {
  id: string;
  cj_product_id: string;
  title: string;
  category: string;
  cost_usd: number;
  shipping_days: number;
  supplier_rating: number;
  stock_status: string;
  images: string[];
  variants: {
    count: number;
    sku: string;
    pid: string;
    sell_price: number;
    retail_price: number;
    profit_margin: number;
    price_range: { min: number; max: number };
    items: Variant[];
    extra_data: {
      description: string;
      video_url: string | null;
      supplier_id: string | null;
      supplier_name: string | null;
      listed_num: number;
      material: string;
      properties: string;
      total_inventory: number;
      inventory_detail: InventoryDetail;
      enrichment_errors: string[];
    };
  };
  fetched_at: string;
}

interface VelocitySnapshot {
  id: string;
  cj_pid: string;
  stock: number;
  listed_num: number;
  price: number;
  stock_delta: number;
  velocity_per_day: number;
  snapshot_at: string;
}

interface VelocityData {
  product: {
    cj_pid: string;
    current_stock: number;
    current_listed_num: number;
    velocity_per_day: number;
    velocity_trend: string;
  };
  snapshots: VelocitySnapshot[];
}

interface AIAnalysis {
  viral_potential: number;
  target_audience: string;
  selling_angles: string[];
  competition_level: string;
  recommendation: string;
  confidence: number;
}

interface Props {
  product: ProductData;
  onClose: () => void;
  apiBase: string;
}

type TabType = 'overview' | 'variants' | 'analytics' | 'velocity' | 'insights' | 'images';

// Background presets for image generation
const BACKGROUND_PRESETS = [
  { name: 'minimal_white', label: 'White', colors: ['#ffffff', '#f5f5f5'], category: 'minimal' },
  { name: 'minimal_gray', label: 'Gray', colors: ['#fafafa', '#e5e5e5'], category: 'minimal' },
  { name: 'warm_cream', label: 'Cream', colors: ['#fffbeb', '#fef3c7'], category: 'minimal' },
  { name: 'cool_blue', label: 'Cool Blue', colors: ['#f0f9ff', '#e0f2fe'], category: 'minimal' },
  { name: 'soft_pink', label: 'Soft Pink', colors: ['#fdf2f8', '#fce7f3'], category: 'minimal' },
  { name: 'mint_fresh', label: 'Mint', colors: ['#f0fdf4', '#dcfce7'], category: 'minimal' },
  { name: 'sunset', label: 'Sunset', colors: ['#f97316', '#ec4899', '#8b5cf6'], category: 'bold' },
  { name: 'ocean', label: 'Ocean', colors: ['#06b6d4', '#3b82f6', '#6366f1'], category: 'bold' },
  { name: 'forest', label: 'Forest', colors: ['#22c55e', '#14b8a6', '#06b6d4'], category: 'bold' },
  { name: 'fire', label: 'Fire', colors: ['#ef4444', '#f97316', '#eab308'], category: 'bold' },
  { name: 'purple_haze', label: 'Purple Haze', colors: ['#a855f7', '#ec4899', '#f43f5e'], category: 'bold' },
  { name: 'midnight', label: 'Midnight', colors: ['#1e1b4b', '#312e81', '#4c1d95'], category: 'bold' },
  { name: 'pure_black', label: 'Black', colors: ['#0a0a0a'], category: 'solid' },
  { name: 'navy', label: 'Navy', colors: ['#1e3a5f'], category: 'solid' },
  { name: 'gold_gradient', label: 'Gold', colors: ['#fef3c7', '#fcd34d', '#f59e0b'], category: 'premium' },
  { name: 'silver_gradient', label: 'Silver', colors: ['#f4f4f5', '#d4d4d8', '#a1a1aa'], category: 'premium' },
  { name: 'rose_gold', label: 'Rose Gold', colors: ['#fdf2f8', '#fbcfe8', '#f9a8d4'], category: 'premium' },
];

interface GeneratedImage {
  preset: string;
  success: boolean;
  filename?: string;
  url?: string;
  error?: string;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export function ProductDetailModal({ product, onClose, apiBase }: Props) {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [velocityData, setVelocityData] = useState<VelocityData | null>(null);
  const [velocityLoading, setVelocityLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysis | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Image generation state
  const [selectedPresets, setSelectedPresets] = useState<string[]>(['minimal_white', 'sunset', 'ocean']);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [imageGenerating, setImageGenerating] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  // Gallery state (all presets at once)
  const [galleryMode, setGalleryMode] = useState(false);
  const [backgroundGallery, setBackgroundGallery] = useState<Record<string, { preset: string; url: string; category: string }> | null>(null);
  const [galleryGenerating, setGalleryGenerating] = useState(false);
  const [selectedGalleryPreset, setSelectedGalleryPreset] = useState<string | null>(null);

  // Multiview state (Zero123)
  const [multiviewData, setMultiviewData] = useState<{
    grid: string;
    views: Array<{ index: number; url: string }>;
    folder: string;
  } | null>(null);
  const [multiviewGenerating, setMultiviewGenerating] = useState(false);
  const [multiviewError, setMultiviewError] = useState<string | null>(null);

  // 2.5D Depth state (MiDaS)
  const [depthData, setDepthData] = useState<{
    image: string;
    depth: string;
    folder: string;
  } | null>(null);
  const [depthGenerating, setDepthGenerating] = useState(false);
  const [depthError, setDepthError] = useState<string | null>(null);

  // 360° Spin Viewer state - SV3D_p single pass for consistent elevation
  const [spinData, setSpinData] = useState<{
    frames: string[];
    frameCount: number;
    spriteSheet?: string;
    metadata?: {
      degreesPerFrame: number;
      elevation: number;
    };
  } | null>(null);
  const [spinGenerating, setSpinGenerating] = useState(false);
  const [spinError, setSpinError] = useState<string | null>(null);

  // Clean images array - handle stringified arrays
  const images = product.images?.map(img => {
    if (img && img.startsWith('[')) {
      try {
        const parsed = JSON.parse(img);
        return Array.isArray(parsed) ? parsed[0] : img;
      } catch { return img; }
    }
    return img;
  }).filter(Boolean) || [];

  const variants = product.variants?.items || [];
  const extraData = product.variants?.extra_data || {};

  // Fetch velocity data when velocity tab is active
  useEffect(() => {
    if (activeTab === 'velocity' && !velocityData && !velocityLoading) {
      fetchVelocityData();
    }
  }, [activeTab]);

  // Fetch AI analysis when insights tab is active
  useEffect(() => {
    if (activeTab === 'insights' && !aiAnalysis && !aiLoading) {
      fetchAIAnalysis();
    }
  }, [activeTab]);


  const fetchVelocityData = async () => {
    setVelocityLoading(true);
    try {
      const res = await fetch(`${apiBase}/velocity/${product.cj_product_id}`);
      if (res.ok) {
        const data = await res.json();
        setVelocityData(data);
      }
    } catch (err) {
      console.error('Failed to fetch velocity:', err);
    } finally {
      setVelocityLoading(false);
    }
  };

  const fetchAIAnalysis = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await fetch(`${apiBase}/analyze-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: product.title,
          category: product.category,
          cost: product.cost_usd,
          variants: variants.length,
          description: extraData.description,
          listed_num: extraData.listed_num,
          total_inventory: extraData.total_inventory
        })
      });
      if (res.ok) {
        const data = await res.json();
        setAiAnalysis(data);
      } else {
        const err = await res.json();
        setAiError(err.error || 'Analysis failed');
      }
    } catch (err) {
      setAiError('Failed to connect to AI service');
    } finally {
      setAiLoading(false);
    }
  };

  // Generate background images
  const generateBackgrounds = async () => {
    if (selectedPresets.length === 0 || !images[0]) return;

    setImageGenerating(true);
    setImageError(null);
    setGeneratedImages([]);

    try {
      const res = await fetch(`${apiBase}/images/generate/background/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: images[0],
          presets: selectedPresets,
          width: 1024,
          height: 1024
        })
      });

      if (res.ok) {
        const data = await res.json();
        setGeneratedImages(data.results || []);
      } else {
        const err = await res.json();
        setImageError(err.error || 'Generation failed');
      }
    } catch (err) {
      setImageError('Failed to connect to image processor');
    } finally {
      setImageGenerating(false);
    }
  };

  const togglePreset = (preset: string) => {
    setSelectedPresets(prev =>
      prev.includes(preset)
        ? prev.filter(p => p !== preset)
        : [...prev, preset]
    );
  };

  // Generate full background gallery (all presets at once)
  const generateGallery = async () => {
    if (!images[0]) return;

    setGalleryGenerating(true);
    setImageError(null);

    try {
      const res = await fetch(`${apiBase}/images/generate/background/gallery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: images[0],
          width: 800,
          height: 800
        })
      });

      if (res.ok) {
        const data = await res.json();
        setBackgroundGallery(data.gallery || {});
        setGalleryMode(true);
        // Select first preset as default
        const firstPreset = Object.keys(data.gallery || {})[0];
        if (firstPreset) setSelectedGalleryPreset(firstPreset);
      } else {
        const err = await res.json();
        setImageError(err.error || 'Gallery generation failed');
      }
    } catch (err) {
      setImageError('Failed to connect to image processor');
    } finally {
      setGalleryGenerating(false);
    }
  };

  // Generate multiview images using Zero123
  const generateMultiview = async () => {
    if (!images[0]) return;

    setMultiviewGenerating(true);
    setMultiviewError(null);

    try {
      const res = await fetch(`${apiBase}/images/generate/multiview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: images[0]
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.multiview?.views?.length > 0) {
          // Extract folder name from grid URL (e.g., /output/zero123_mv_xxxx/grid.png)
          const gridUrl = data.multiview.grid.url;
          const folderMatch = gridUrl.match(/\/output\/([^/]+)\//);
          const folderName = folderMatch ? folderMatch[1] : '';

          setMultiviewData({
            grid: `${apiBase}/images/multiview-output${gridUrl.replace('/output', '')}`,
            views: data.multiview.views.map((v: { index: number; url: string }) => ({
              index: v.index,
              url: `${apiBase}/images/multiview-output${v.url.replace('/output', '')}`
            })),
            folder: folderName
          });
        } else {
          setMultiviewError('No views generated');
        }
      } else {
        const err = await res.json();
        setMultiviewError(err.error || 'Multiview generation failed');
      }
    } catch (err) {
      setMultiviewError('Failed to connect to multiview processor');
    } finally {
      setMultiviewGenerating(false);
    }
  };

  // Generate 2.5D depth map using MiDaS
  const generateDepth = async () => {
    if (!images[0]) return;

    setDepthGenerating(true);
    setDepthError(null);

    try {
      const res = await fetch(`${apiBase}/images/generate/depth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: images[0],
          output_prefix: `product_${product.cj_product_id}`
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.depth_data) {
          setDepthData({
            image: `${apiBase}/images/depth-output${data.depth_data.image.replace('/output', '')}`,
            depth: `${apiBase}/images/depth-output${data.depth_data.depth.replace('/output', '')}`,
            folder: data.depth_data.folder
          });
        } else {
          setDepthError('No depth map generated');
        }
      } else {
        const err = await res.json();
        setDepthError(err.error || 'Depth generation failed');
      }
    } catch (err) {
      setDepthError('Failed to connect to depth processor');
    } finally {
      setDepthGenerating(false);
    }
  };

  // Generate 360° spin frames using SV3D_p single pass
  const generateSpin = async () => {
    if (!images[0]) return;

    setSpinGenerating(true);
    setSpinError(null);

    try {
      const res = await fetch(`${apiBase}/images/generate/spin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: images[0],
          output_prefix: `product_${product.cj_product_id}`,
          elevation: 20.0,  // Consistent camera elevation
          num_frames: 24,   // Smooth rotation
          remove_bg: true
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.frames?.length > 0) {
          // Convert frame paths to full URLs
          const frameUrls = data.frames.map((path: string) =>
            `${apiBase}/images/spin-output${path.replace('/output/', '/')}`
          );

          setSpinData({
            frames: frameUrls,
            frameCount: data.frame_count,
            spriteSheet: data.sprite_sheet
              ? `${apiBase}/images/spin-output${data.sprite_sheet.replace('/output/', '/')}`
              : undefined,
            metadata: {
              degreesPerFrame: data.metadata?.degrees_per_frame || 360 / data.frame_count,
              elevation: data.metadata?.elevation || 20
            }
          });
        } else {
          setSpinError(data.error || 'No frames generated');
        }
      } else {
        const err = await res.json();
        setSpinError(err.error || 'Spin generation failed');
      }
    } catch (err) {
      setSpinError('Failed to connect to image processor');
    } finally {
      setSpinGenerating(false);
    }
  };

  // Calculate profit metrics
  const costPrice = product.cost_usd || product.variants?.sell_price || 0;
  const retailPrice = product.variants?.retail_price || costPrice * 3;
  const grossProfit = retailPrice - costPrice;
  const grossMargin = retailPrice > 0 ? (grossProfit / retailPrice) * 100 : 0;

  // Estimated fees
  const shippingCost = costPrice < 10 ? 3.50 : costPrice < 30 ? 5.00 : 8.00;
  const platformFee = retailPrice * 0.029 + 0.30; // Stripe
  const marketingCost = retailPrice * 0.25; // 25% of revenue
  const netProfit = grossProfit - shippingCost - platformFee - marketingCost;
  const netMargin = retailPrice > 0 ? (netProfit / retailPrice) * 100 : 0;
  const roi = costPrice > 0 ? (netProfit / costPrice) * 100 : 0;

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <Package className="w-4 h-4" /> },
    { id: 'variants', label: `Variants (${variants.length})`, icon: <Box className="w-4 h-4" /> },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'velocity', label: 'Velocity', icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'insights', label: 'AI Insights', icon: <Sparkles className="w-4 h-4" /> },
    { id: 'images', label: 'Images', icon: <Image className="w-4 h-4" /> },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-[95vw] max-w-6xl h-[90vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-gray-900/80">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-gray-800 overflow-hidden flex-shrink-0">
              {images[0] ? (
                <img src={images[0]} alt="" className="w-full h-full object-cover" />
              ) : (
                <Package className="w-6 h-6 m-3 text-gray-600" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white line-clamp-1">{product.title}</h2>
              <p className="text-sm text-gray-400">{product.category}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700 bg-gray-900/50">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-400 bg-blue-500/10'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-3 gap-6">
              {/* Image Gallery */}
              <div className="col-span-1">
                <div className="aspect-square bg-gray-800 rounded-lg overflow-hidden mb-3">
                  {images[selectedImageIndex] ? (
                    <img
                      src={images[selectedImageIndex]}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-16 h-16 text-gray-700" />
                    </div>
                  )}
                </div>
                {images.length > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedImageIndex(i => Math.max(0, i - 1))}
                      disabled={selectedImageIndex === 0}
                      className="p-1 bg-gray-800 rounded disabled:opacity-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="flex-1 flex gap-1 overflow-x-auto">
                      {images.slice(0, 8).map((img, i) => (
                        <button
                          key={i}
                          onClick={() => setSelectedImageIndex(i)}
                          className={`w-12 h-12 rounded flex-shrink-0 overflow-hidden border-2 ${
                            i === selectedImageIndex ? 'border-blue-500' : 'border-transparent'
                          }`}
                        >
                          <img src={img} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setSelectedImageIndex(i => Math.min(images.length - 1, i + 1))}
                      disabled={selectedImageIndex === images.length - 1}
                      className="p-1 bg-gray-800 rounded disabled:opacity-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {extraData.video_url && (
                  <a
                    href={extraData.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 mt-3 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded text-red-400 text-sm transition-colors"
                  >
                    <Video className="w-4 h-4" />
                    Watch Product Video
                  </a>
                )}
              </div>

              {/* Quick Stats */}
              <div className="col-span-2 space-y-4">
                {/* Price & Profit Row */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="text-xs text-gray-500 mb-1">Cost Price</div>
                    <div className="text-xl font-bold text-white">${costPrice.toFixed(2)}</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="text-xs text-gray-500 mb-1">Retail Price</div>
                    <div className="text-xl font-bold text-green-400">${retailPrice.toFixed(2)}</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="text-xs text-gray-500 mb-1">Gross Margin</div>
                    <div className={`text-xl font-bold ${grossMargin >= 50 ? 'text-green-400' : grossMargin >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {grossMargin.toFixed(1)}%
                    </div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="text-xs text-gray-500 mb-1">Net Profit</div>
                    <div className={`text-xl font-bold ${netProfit > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${netProfit.toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Inventory & Competition Row */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                      <Box className="w-3 h-3" />
                      Total Inventory
                    </div>
                    <div className="text-lg font-semibold text-white">
                      {(extraData.total_inventory || 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      across {variants.length} variants
                    </div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                      <Users className="w-3 h-3" />
                      Sellers Listing
                    </div>
                    <div className="text-lg font-semibold text-white">
                      {(extraData.listed_num || 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {extraData.listed_num > 500 ? 'High competition' : extraData.listed_num > 100 ? 'Medium competition' : 'Low competition'}
                    </div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                      <Truck className="w-3 h-3" />
                      Shipping
                    </div>
                    <div className="text-lg font-semibold text-white">
                      {product.shipping_days} days
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Est. ${shippingCost.toFixed(2)} cost
                    </div>
                  </div>
                </div>

                {/* Description */}
                {extraData.description && (
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="text-xs text-gray-500 mb-2">Product Description</div>
                    <div
                      className="text-sm text-gray-300 max-h-40 overflow-y-auto prose prose-invert prose-sm"
                      dangerouslySetInnerHTML={{ __html: extraData.description }}
                    />
                  </div>
                )}

                {/* Quick Actions */}
                <div className="flex gap-3">
                  <a
                    href={`https://cjdropshipping.com/product/-p-${product.cj_product_id}.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View on CJ
                  </a>
                  <div className="text-xs text-gray-500 flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    Fetched {new Date(product.fetched_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Variants Tab */}
          {activeTab === 'variants' && (
            <div className="space-y-4">
              {variants.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {variants.map((variant, i) => {
                    // Find inventory for this variant
                    const variantInv = extraData.inventory_detail?.perVariant?.find(
                      v => v.vid === variant.vid
                    );
                    const totalInv = variantInv?.inventory?.reduce(
                      (sum, inv) => sum + (inv.totalInventory || 0), 0
                    ) || variant.inventory || 0;

                    return (
                      <div
                        key={variant.vid || i}
                        className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden"
                      >
                        <div className="aspect-square bg-gray-900">
                          {variant.image ? (
                            <img src={variant.image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-8 h-8 text-gray-700" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <div className="text-sm font-medium text-white line-clamp-2 mb-2">
                            {variant.name?.split(' ').slice(-2).join(' ') || `Variant ${i + 1}`}
                          </div>
                          <div className="text-xs text-gray-500 mb-2">{variant.sku}</div>
                          <div className="flex justify-between items-center">
                            <div className="text-lg font-bold text-green-400">
                              ${variant.price.toFixed(2)}
                            </div>
                            <div className={`text-xs px-2 py-1 rounded ${
                              totalInv > 1000 ? 'bg-green-500/20 text-green-400' :
                              totalInv > 100 ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {totalInv.toLocaleString()} in stock
                            </div>
                          </div>
                          {variant.weight > 0 && (
                            <div className="text-xs text-gray-500 mt-2">
                              {variant.weight}g • {variant.dimensions?.length}x{variant.dimensions?.width}x{variant.dimensions?.height}mm
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No variant data available
                </div>
              )}

              {/* Inventory by Country */}
              {extraData.inventory_detail?.summary && extraData.inventory_detail.summary.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-400 mb-3">Inventory by Warehouse</h3>
                  <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-800">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs text-gray-500">Warehouse</th>
                          <th className="px-4 py-2 text-right text-xs text-gray-500">CJ Stock</th>
                          <th className="px-4 py-2 text-right text-xs text-gray-500">Factory Stock</th>
                          <th className="px-4 py-2 text-right text-xs text-gray-500">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {extraData.inventory_detail.summary.map((inv, i) => (
                          <tr key={i} className="border-t border-gray-700">
                            <td className="px-4 py-2 text-sm text-white">{inv.countryNameEn}</td>
                            <td className="px-4 py-2 text-sm text-gray-400 text-right">{inv.cjInventoryNum.toLocaleString()}</td>
                            <td className="px-4 py-2 text-sm text-gray-400 text-right">{inv.factoryInventoryNum.toLocaleString()}</td>
                            <td className="px-4 py-2 text-sm text-white text-right font-medium">{inv.totalInventoryNum.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              {/* Profit Breakdown */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-5">
                  <h3 className="text-sm font-medium text-gray-400 mb-4">Profit Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Retail Price</span>
                      <span className="text-sm font-medium text-white">${retailPrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-red-400">
                      <span className="text-sm">− Product Cost</span>
                      <span className="text-sm font-medium">−${costPrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-red-400">
                      <span className="text-sm">− Shipping Cost</span>
                      <span className="text-sm font-medium">−${shippingCost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-red-400">
                      <span className="text-sm">− Platform Fees (2.9%+$0.30)</span>
                      <span className="text-sm font-medium">−${platformFee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-red-400">
                      <span className="text-sm">− Marketing (25%)</span>
                      <span className="text-sm font-medium">−${marketingCost.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-700 pt-3 flex justify-between items-center">
                      <span className="text-sm font-medium text-white">Net Profit per Unit</span>
                      <span className={`text-lg font-bold ${netProfit > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ${netProfit.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-5">
                  <h3 className="text-sm font-medium text-gray-400 mb-4">Key Metrics</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-gray-400">Gross Margin</span>
                        <span className={`text-sm font-bold ${grossMargin >= 50 ? 'text-green-400' : grossMargin >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {grossMargin.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${grossMargin >= 50 ? 'bg-green-500' : grossMargin >= 30 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(100, grossMargin)}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-gray-400">Net Margin</span>
                        <span className={`text-sm font-bold ${netMargin >= 20 ? 'text-green-400' : netMargin >= 10 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {netMargin.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${netMargin >= 20 ? 'bg-green-500' : netMargin >= 10 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(100, Math.max(0, netMargin))}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-gray-400">ROI</span>
                        <span className={`text-sm font-bold ${roi >= 50 ? 'text-green-400' : roi >= 20 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {roi.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${roi >= 50 ? 'bg-green-500' : roi >= 20 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(100, Math.max(0, roi / 2))}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Revenue Projections */}
              <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-5">
                <h3 className="text-sm font-medium text-gray-400 mb-4">Revenue Projections</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={[
                        { units: 10, revenue: retailPrice * 10, profit: netProfit * 10 },
                        { units: 25, revenue: retailPrice * 25, profit: netProfit * 25 },
                        { units: 50, revenue: retailPrice * 50, profit: netProfit * 50 },
                        { units: 100, revenue: retailPrice * 100, profit: netProfit * 100 },
                        { units: 200, revenue: retailPrice * 200, profit: netProfit * 200 },
                        { units: 500, revenue: retailPrice * 500, profit: netProfit * 500 },
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="units" tick={{ fill: '#9ca3af', fontSize: 12 }} tickFormatter={(v) => `${v} units`} />
                      <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#9ca3af' }}
                      />
                      <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                      <Area type="monotone" dataKey="profit" name="Net Profit" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-blue-500" />
                    <span className="text-xs text-gray-400">Revenue</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-green-500" />
                    <span className="text-xs text-gray-400">Net Profit</span>
                  </div>
                </div>
              </div>

              {/* Break-even Analysis */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4 text-center">
                  <div className="text-2xl font-bold text-white">
                    {netProfit > 0 ? Math.ceil(100 / netProfit) : '∞'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Units to earn $100</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4 text-center">
                  <div className="text-2xl font-bold text-white">
                    {netProfit > 0 ? Math.ceil(1000 / netProfit) : '∞'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Units to earn $1,000</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4 text-center">
                  <div className="text-2xl font-bold text-white">
                    ${(netProfit * 100).toFixed(0)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Profit from 100 sales</div>
                </div>
              </div>
            </div>
          )}

          {/* Velocity Tab */}
          {activeTab === 'velocity' && (
            <div className="space-y-6">
              {velocityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  <span className="ml-3 text-gray-400">Loading velocity data...</span>
                </div>
              ) : velocityData?.snapshots?.length ? (
                <>
                  {/* Current Status */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
                      <div className="text-xs text-gray-500 mb-1">Current Stock</div>
                      <div className="text-2xl font-bold text-white">
                        {(velocityData.product.current_stock || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
                      <div className="text-xs text-gray-500 mb-1">Velocity</div>
                      <div className={`text-2xl font-bold ${
                        velocityData.product.velocity_trend === 'hot' ? 'text-red-400' :
                        velocityData.product.velocity_trend === 'warm' ? 'text-yellow-400' :
                        'text-gray-400'
                      }`}>
                        {velocityData.product.velocity_per_day?.toFixed(1) || 0}/day
                      </div>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
                      <div className="text-xs text-gray-500 mb-1">Trend</div>
                      <div className={`text-xl font-bold capitalize ${
                        velocityData.product.velocity_trend === 'hot' ? 'text-red-400' :
                        velocityData.product.velocity_trend === 'warm' ? 'text-yellow-400' :
                        velocityData.product.velocity_trend === 'slow' ? 'text-blue-400' :
                        'text-gray-400'
                      }`}>
                        {velocityData.product.velocity_trend || 'Unknown'}
                      </div>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
                      <div className="text-xs text-gray-500 mb-1">Snapshots</div>
                      <div className="text-2xl font-bold text-white">
                        {velocityData.snapshots.length}
                      </div>
                    </div>
                  </div>

                  {/* Stock Over Time Chart */}
                  <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-5">
                    <h3 className="text-sm font-medium text-gray-400 mb-4">Stock Level Over Time</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={velocityData.snapshots.slice().reverse()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis
                            dataKey="snapshot_at"
                            tick={{ fill: '#9ca3af', fontSize: 11 }}
                            tickFormatter={(v) => new Date(v).toLocaleDateString()}
                          />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                            labelFormatter={(v) => new Date(v).toLocaleString()}
                          />
                          <Line
                            type="monotone"
                            dataKey="stock"
                            name="Stock"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            dot={{ fill: '#3b82f6', r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Snapshot History */}
                  <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-700">
                      <h3 className="text-sm font-medium text-gray-400">Snapshot History</h3>
                    </div>
                    <table className="w-full">
                      <thead className="bg-gray-800">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs text-gray-500">Date</th>
                          <th className="px-4 py-2 text-right text-xs text-gray-500">Stock</th>
                          <th className="px-4 py-2 text-right text-xs text-gray-500">Change</th>
                          <th className="px-4 py-2 text-right text-xs text-gray-500">Velocity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {velocityData.snapshots.slice(0, 10).map((snap, i) => (
                          <tr key={snap.id} className="border-t border-gray-700">
                            <td className="px-4 py-2 text-sm text-white">
                              {new Date(snap.snapshot_at).toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-400 text-right">
                              {snap.stock?.toLocaleString() || '-'}
                            </td>
                            <td className={`px-4 py-2 text-sm text-right ${
                              snap.stock_delta > 0 ? 'text-red-400' :
                              snap.stock_delta < 0 ? 'text-green-400' : 'text-gray-400'
                            }`}>
                              {snap.stock_delta ? (snap.stock_delta > 0 ? `-${snap.stock_delta}` : `+${Math.abs(snap.stock_delta)}`) : '-'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-400 text-right">
                              {snap.velocity_per_day ? `${snap.velocity_per_day}/day` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <TrendingUp className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                  <div className="text-gray-400 mb-2">No velocity data yet</div>
                  <div className="text-sm text-gray-500">
                    Velocity tracking runs every 6 hours. Save this product to start tracking.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* AI Insights Tab */}
          {activeTab === 'insights' && (
            <div className="space-y-6">
              {aiLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                  <span className="ml-3 text-gray-400">Analyzing product with AI...</span>
                </div>
              ) : aiError ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <div className="text-red-400 mb-2">{aiError}</div>
                  <button
                    onClick={fetchAIAnalysis}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm"
                  >
                    Retry Analysis
                  </button>
                </div>
              ) : aiAnalysis ? (
                <>
                  {/* Confidence Score */}
                  <div className="flex items-center gap-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm text-purple-400 mb-1">AI Confidence Score</div>
                      <div className="flex items-center gap-3">
                        <div className="text-3xl font-bold text-white">{aiAnalysis.confidence}%</div>
                        <div className="flex-1 bg-gray-700 rounded-full h-3">
                          <div
                            className={`h-3 rounded-full ${
                              aiAnalysis.confidence >= 70 ? 'bg-green-500' :
                              aiAnalysis.confidence >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${aiAnalysis.confidence}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    {aiAnalysis.confidence >= 70 ? (
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    ) : (
                      <AlertCircle className="w-8 h-8 text-yellow-500" />
                    )}
                  </div>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
                      <div className="text-xs text-gray-500 mb-1">Viral Potential</div>
                      <div className="flex items-center gap-2">
                        <div className={`text-2xl font-bold ${
                          aiAnalysis.viral_potential >= 7 ? 'text-green-400' :
                          aiAnalysis.viral_potential >= 5 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {aiAnalysis.viral_potential}/10
                        </div>
                        <Sparkles className={`w-5 h-5 ${
                          aiAnalysis.viral_potential >= 7 ? 'text-green-400' : 'text-gray-600'
                        }`} />
                      </div>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
                      <div className="text-xs text-gray-500 mb-1">Competition Level</div>
                      <div className={`text-xl font-bold ${
                        aiAnalysis.competition_level === 'Low' ? 'text-green-400' :
                        aiAnalysis.competition_level === 'Medium' ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {aiAnalysis.competition_level}
                      </div>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
                      <div className="text-xs text-gray-500 mb-1">Recommendation</div>
                      <div className={`text-xl font-bold ${
                        aiAnalysis.recommendation === 'Strong Buy' ? 'text-green-400' :
                        aiAnalysis.recommendation === 'Buy' ? 'text-blue-400' :
                        aiAnalysis.recommendation === 'Hold' ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {aiAnalysis.recommendation}
                      </div>
                    </div>
                  </div>

                  {/* Target Audience */}
                  <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-5">
                    <h3 className="text-sm font-medium text-gray-400 mb-3">Target Audience</h3>
                    <p className="text-white">{aiAnalysis.target_audience}</p>
                  </div>

                  {/* Selling Angles */}
                  <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-5">
                    <h3 className="text-sm font-medium text-gray-400 mb-3">Selling Angles</h3>
                    <div className="space-y-2">
                      {aiAnalysis.selling_angles?.map((angle, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-300">{angle}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                  <div className="text-gray-400 mb-4">AI analysis not yet generated</div>
                  <button
                    onClick={fetchAIAnalysis}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm"
                  >
                    Generate AI Insights
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Images Tab */}
          {activeTab === 'images' && (
            <div className="space-y-6">
              {/* Source Image & Quick Actions */}
              <div className="flex items-start gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <div className="w-24 h-24 rounded-lg bg-gray-900 overflow-hidden flex-shrink-0">
                  {images[0] ? (
                    <img src={images[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-8 h-8 text-gray-700" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-white mb-1">Source Image</div>
                  <div className="text-xs text-gray-400 mb-3">
                    Generate professional backgrounds and 360° orbit views
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={generateGallery}
                      disabled={galleryGenerating || !images[0]}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-all ${
                        galleryGenerating
                          ? 'bg-gray-700 text-gray-500'
                          : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'
                      }`}
                    >
                      {galleryGenerating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating Gallery...
                        </>
                      ) : (
                        <>
                          <Image className="w-4 h-4" />
                          Generate All Backgrounds
                        </>
                      )}
                    </button>
                    <button
                      onClick={generateSpin}
                      disabled={spinGenerating || !images[0]}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-all ${
                        spinGenerating
                          ? 'bg-gray-700 text-gray-500'
                          : 'bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 hover:from-purple-700 hover:via-pink-700 hover:to-orange-600 text-white shadow-lg'
                      }`}
                      title="Generate interactive 360° spin viewer with drag-to-rotate"
                    >
                      {spinGenerating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating 360°...
                        </>
                      ) : (
                        <>
                          <View className="w-4 h-4" />
                          360° Spin Viewer
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {(imageError || spinError) && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {imageError || spinError}
                </div>
              )}

              {/* 360° Spin Viewer */}
              {spinData && spinData.frames.length > 0 && (
                <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                    <h3 className="text-sm font-medium text-white flex items-center gap-2">
                      <View className="w-4 h-4 text-purple-400" />
                      360° Interactive Spin Viewer
                      <span className="text-xs text-gray-500 ml-2">
                        {spinData.frameCount} frames
                      </span>
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        Elevation: {spinData.metadata?.elevation || 20}°
                      </span>
                      {spinData.spriteSheet && (
                        <a
                          href={spinData.spriteSheet}
                          download
                          className="flex items-center gap-1 px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs text-gray-300 transition-colors"
                          title="Download sprite sheet"
                        >
                          <Download className="w-3 h-3" />
                          Sprite
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    <ProductSpinViewer
                      frames={spinData.frames}
                      degreesPerFrame={spinData.metadata?.degreesPerFrame || 360 / spinData.frameCount}
                      autoRotateSpeed={30}
                      showControls={true}
                      enableFullscreen={true}
                    />
                  </div>
                </div>
              )}

              {/* Background Gallery View */}
              {galleryMode && backgroundGallery && Object.keys(backgroundGallery).length > 0 && (
                <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                    <h3 className="text-sm font-medium text-white">Background Gallery</h3>
                    <button
                      onClick={() => setGalleryMode(false)}
                      className="text-xs text-gray-400 hover:text-white"
                    >
                      Show Presets
                    </button>
                  </div>

                  {/* Selected Preview */}
                  {selectedGalleryPreset && backgroundGallery[selectedGalleryPreset] && (
                    <div className="p-4 border-b border-gray-700">
                      <div className="flex gap-4">
                        <div className="w-80 h-80 rounded-lg overflow-hidden bg-gray-900 flex-shrink-0">
                          <img
                            src={`${apiBase}/images${backgroundGallery[selectedGalleryPreset].url.replace('/output/', '/gallery-output/')}`}
                            alt={selectedGalleryPreset}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="text-lg font-medium text-white mb-1">
                            {BACKGROUND_PRESETS.find(p => p.name === selectedGalleryPreset)?.label || selectedGalleryPreset}
                          </div>
                          <div className="text-sm text-gray-400 mb-4 capitalize">
                            {backgroundGallery[selectedGalleryPreset].category} preset
                          </div>
                          <a
                            href={`${apiBase}/images${backgroundGallery[selectedGalleryPreset].url.replace('/output/', '/gallery-output/')}`}
                            download
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm"
                          >
                            <Download className="w-4 h-4" />
                            Download Full Size
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Thumbnail Grid */}
                  <div className="p-4">
                    <div className="text-xs text-gray-500 mb-3">
                      {Object.keys(backgroundGallery).length} variants • Click to preview
                    </div>
                    {['minimal', 'bold', 'solid', 'premium'].map(category => {
                      const categoryItems = Object.entries(backgroundGallery).filter(([, v]) => v.category === category);
                      if (categoryItems.length === 0) return null;

                      return (
                        <div key={category} className="mb-4">
                          <div className="text-xs text-gray-500 uppercase mb-2">{category}</div>
                          <div className="flex flex-wrap gap-2">
                            {categoryItems.map(([preset, data]) => (
                              <button
                                key={preset}
                                onClick={() => setSelectedGalleryPreset(preset)}
                                className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                  selectedGalleryPreset === preset
                                    ? 'border-blue-500 ring-2 ring-blue-500/30'
                                    : 'border-gray-700 hover:border-gray-500'
                                }`}
                              >
                                <img
                                  src={`${apiBase}/images${data.url.replace('/output/', '/gallery-output/')}`}
                                  alt={preset}
                                  className="w-full h-full object-cover"
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Manual Preset Selection (shown when not in gallery mode) */}
              {!galleryMode && (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-400">Select Background Presets</h3>
                      <span className="text-xs text-gray-500">{selectedPresets.length} selected</span>
                    </div>

                    {/* Categories */}
                    {['minimal', 'bold', 'solid', 'premium'].map(category => (
                      <div key={category} className="mb-4">
                        <div className="text-xs text-gray-500 uppercase mb-2">{category}</div>
                        <div className="flex flex-wrap gap-2">
                          {BACKGROUND_PRESETS.filter(p => p.category === category).map(preset => {
                            const isSelected = selectedPresets.includes(preset.name);
                            const gradientStyle = preset.colors.length > 1
                              ? { background: `linear-gradient(135deg, ${preset.colors.join(', ')})` }
                              : { backgroundColor: preset.colors[0] };

                            return (
                              <button
                                key={preset.name}
                                onClick={() => togglePreset(preset.name)}
                                className={`relative flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                                  isSelected
                                    ? 'border-blue-500 bg-blue-500/10'
                                    : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
                                }`}
                              >
                                <div
                                  className="w-6 h-6 rounded border border-white/20"
                                  style={gradientStyle}
                                />
                                <span className="text-sm text-gray-300">{preset.label}</span>
                                {isSelected && (
                                  <Check className="w-4 h-4 text-blue-400" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Generate Selected Button */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={generateBackgrounds}
                      disabled={imageGenerating || selectedPresets.length === 0 || !images[0]}
                      className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                        imageGenerating || selectedPresets.length === 0
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                      }`}
                    >
                      {imageGenerating ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Image className="w-5 h-5" />
                          Generate {selectedPresets.length} Background{selectedPresets.length !== 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                    {imageGenerating && (
                      <span className="text-sm text-gray-400">
                        This may take 30-60 seconds for background removal...
                      </span>
                    )}
                  </div>

                  {/* Generated Images (from manual selection) */}
                  {generatedImages.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-400 mb-3">Generated Images</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {generatedImages.map((img, i) => {
                          const preset = BACKGROUND_PRESETS.find(p => p.name === img.preset);
                          return (
                            <div
                              key={i}
                              className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden"
                            >
                              {img.success ? (
                                <>
                                  <div className="aspect-square bg-gray-900">
                                    <img
                                      src={`${apiBase}/images${img.url}`}
                                      alt={preset?.label || img.preset}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <div className="p-3 flex items-center justify-between">
                                    <span className="text-sm text-gray-300">{preset?.label || img.preset}</span>
                                    <a
                                      href={`${apiBase}/images${img.url}`}
                                      download={img.filename}
                                      className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded text-white transition-colors"
                                      title="Download"
                                    >
                                      <Download className="w-4 h-4" />
                                    </a>
                                  </div>
                                </>
                              ) : (
                                <div className="aspect-square flex items-center justify-center bg-gray-900">
                                  <div className="text-center p-4">
                                    <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                                    <div className="text-xs text-gray-500">{img.error || 'Failed'}</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Tips */}
                  {generatedImages.length === 0 && !imageGenerating && !backgroundGallery && (
                    <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
                      <div className="text-xs text-gray-500 mb-2">Tips:</div>
                      <ul className="text-xs text-gray-500 space-y-1">
                        <li>• <strong>Generate All Backgrounds</strong> creates every preset at once - best for comparing options</li>
                        <li>• <strong>Generate 3D Model</strong> creates an interactive 3D model using AI (TripoSR)</li>
                        <li>• Minimal backgrounds work best for e-commerce listings</li>
                        <li>• Bold gradients are great for social media marketing</li>
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductDetailModal;
